/**
 * External dependencies
 */
import clsx from 'clsx';

/**
 * WordPress dependencies
 */
import { _x } from '@safe-wordpress/i18n';

/**
 * Internal dependencies
 */
import { FontFamily, getEditorSettings } from '../../utils';
import { CustomSelectControl } from '../../../components';

type FontFamilyControlProps = {
	readonly __next40pxDefaultSize?: boolean;
	readonly __nextHasNoMarginBottom?: boolean;
	readonly value?: string;
	readonly onChange: ( value?: string ) => void;
	readonly fontFamilies?: Array< FontFamily >;
	readonly className?: string;
	readonly size?: 'default' | 'small' | '__unstable-large';
	readonly disabled?: boolean;
};
export function FontFamilyControl( {
	/** Start opting into the larger default height that will become the default size in a future version. */
	__next40pxDefaultSize = false,
	/** Start opting into the new margin-free styles that will become the default in a future version. */
	__nextHasNoMarginBottom = false,
	value = '',
	onChange,
	fontFamilies,
	className,
	disabled,
	...props
}: FontFamilyControlProps ): JSX.Element | null {
	const settings = getEditorSettings();
	const blockLevelFontFamilies = Object.values(
		settings.typography.fontFamilies
	).flatMap( ( x ) => x );

	if ( ! fontFamilies ) {
		fontFamilies = blockLevelFontFamilies;
	}

	if ( ! fontFamilies || fontFamilies.length === 0 ) {
		return null;
	}

	const options = [
		{
			key: '',
			name: _x( 'Default', 'text', 'nelio-ab-testing' ),
		},
		...fontFamilies
			.filter( ( ff ): ff is Required< FontFamily > => !! ff.fontFamily )
			.map( ( { fontFamily, name } ) => ( {
				key: fontFamily,
				name: name || fontFamily,
				style: { fontFamily },
			} ) ),
	];

	const selectedValue = options.find( ( option ) => option.key === value );
	return (
		<CustomSelectControl
			disabled={ disabled }
			__next40pxDefaultSize={ __next40pxDefaultSize }
			label={ _x( 'Font', 'text', 'nelio-ab-testing' ) }
			value={ selectedValue }
			onChange={ ( {
				selectedItem,
			}: {
				selectedItem: {
					key: string | undefined;
					name: string | undefined;
				};
			} ) => onChange( selectedItem.key ) }
			options={ options }
			className={ clsx( 'block-editor-font-family-control', className, {
				'is-next-has-no-margin-bottom': __nextHasNoMarginBottom,
				'nab-components--disabled': disabled,
			} ) }
			{ ...props }
		/>
	);
}
